import { ScrollView, StyleSheet, View } from "react-native";
import { Feature } from "util/stdtypes";
import { colorKilauea1, colorMum, colorSaguaro2, colorSurfaceInfo, colorSurfaceSecondary, colorTextHint } from "component/color";
import { useDatastore } from "util/datastore";
import { Heading, UtilityText } from "component/text";
import { HorizBox } from "component/basics";
import { Banner } from "component/banner";
import { WarningAltFilled } from "@carbon/icons-react";
import { formatDate } from "component/date";
import { SuspensionStatusType, useMySuspensions } from "open-modules/zdf/component/suspension/suspension";

export const WarnBlockOverviewFeature:Feature = {
    name:"Warn & Block Overview",
    key:"warnblockoverviewfeature",
    config:{
        accountWidgets: [WarnBlockDisplay]
    }
}

const WarnAndBlockStyle = StyleSheet.create({
    outer: { 
        marginTop: 40,
        display: "flex", 
        flexDirection: "column", 
        gap: 8, 
        justifyContent: "flex-start" 
    },
    scroll: { 
        display: "flex", 
        gap: 16, 
        flexDirection: "column", 
        marginTop: 16, 
        maxHeight: 400 
    },
    outerCard: { 
        display: "flex", 
        width: "100%", 
        flexDirection: "row", 
        alignItems: "center", 
        gap: 12, backgroundColor: colorSurfaceSecondary, 
        borderRadius: 8, 
        margin: 6 },
    innerCard: { 
        justifyContent: "center", 
        alignItems: "center", 
        display: "flex", 
        margin: 12,
        width: 48, 
        height: 48, 
        borderRadius: 4 
    },
    innerCardColumn: {
        display: "flex", 
        flexDirection: "column", 
        gap: 4
    }
})

export function WarnBlockDisplay() {
    const s = WarnAndBlockStyle;
    const datastore = useDatastore()
    const mySuspensions = useMySuspensions();

    const renderedSuspensions = mySuspensions.all.filter(
        (s => (s.suspensionType !== SuspensionStatusType.UNWARNED))
    )

    return <View style={s.outer}>
        <Heading type="small" label="Warnings and blocks" />
        <HorizBox gap={8} center>
            <UtilityText label="🔒 Only visible to you" color={colorTextHint} />
        </HorizBox>
        <ScrollView style={s.scroll}>
            {renderedSuspensions.length <= 0 && <Banner color={colorSurfaceInfo} >
                <UtilityText text="No suspensions yet" />
            </Banner>}
            {renderedSuspensions.map((event) => <WarnBlockCard key={event?.key} time={event?.time} type={event.suspensionType} />)}
        </ScrollView>
    </View>
}

const suspensionStatusLabels: Record<SuspensionStatusType, string> = {
    [SuspensionStatusType.WARNED]: "A moderator warned you",
    [SuspensionStatusType.UNWARNED]: "A moderator unwarned you", // Not used
    [SuspensionStatusType.BLOCKED]: "A moderator blocked you",
    [SuspensionStatusType.UNBLOCKED]: "A moderator unblocked you",
}

const suspensionStatusColors: Record<SuspensionStatusType, string> = {
    [SuspensionStatusType.WARNED]: colorSaguaro2,
    [SuspensionStatusType.UNWARNED]: colorMum, // Not used
    [SuspensionStatusType.BLOCKED]: colorKilauea1,
    [SuspensionStatusType.UNBLOCKED]: colorMum,
}

export function WarnBlockCard({ type, time = 0 }: {type: SuspensionStatusType, time: number}) {
    const s = WarnAndBlockStyle;

    const cardLabel = suspensionStatusLabels[type];
    const cardColor = suspensionStatusColors[type];

    return <View style={s.outerCard}>
        <View style={{...s.innerCard, backgroundColor: cardColor}}><WarningAltFilled /></View>
        <View style={s.innerCardColumn}>
            <UtilityText weight="strong" type="small" label={cardLabel} />
            <UtilityText text={formatDate(time)} type="tiny" />
        </View>
    </View>
}